import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getRevisionRapidaSaludRecordDetail,
  updateRevisionRapidaSaludRecord,
} from "./revisionRapidaSaludApi";
import {
  buildRevisionDetailChanges,
  createRevisionDetailDraft,
  getRevisionDetailError,
  hasRevisionDetailChanges,
  isRevisionDetailContextValid,
  normalizeRevisionFieldInput,
  validateRevisionDetailDraft,
} from "./revisionRapidaSaludDetailUtils";
import { getRevisionRecordStatus } from "./revisionRapidaSaludUtils";

const SUCCESS_MESSAGES = Object.freeze({
  save: "Cambios guardados",
  save_and_verify: "Registro verificado correctamente",
  mark_error: "Registro marcado con error",
});

const useRevisionRegistroDraft = ({
  convocatoriaId,
  registroId,
  enabled = true,
  onDirtyChange,
  onMutationSuccess,
}) => {
  const requestSequence = useRef(0);
  const mutationInFlight = useRef(false);
  const [serverSnapshot, setServerSnapshot] = useState(null);
  const [draft, setDraft] = useState(null);
  const [initialLoading, setInitialLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [stateDialog, setStateDialog] = useState(null);

  const applyServerDetail = useCallback(
    (detail) => {
      if (!isRevisionDetailContextValid(detail, convocatoriaId)) {
        setServerSnapshot(null);
        setDraft(null);
        setError("No tienes acceso a este registro o ya no está disponible.");
        return false;
      }
      setServerSnapshot(detail);
      setDraft(createRevisionDetailDraft(detail));
      setFieldErrors({});
      setError("");
      return true;
    },
    [convocatoriaId]
  );

  const loadDetail = useCallback(
    async ({ initial = false } = {}) => {
      if (!enabled || registroId === null || registroId === undefined || registroId === "") return null;
      const requestId = ++requestSequence.current;
      if (initial) setInitialLoading(true);
      else setRefreshing(true);
      setError("");

      try {
        const detail = await getRevisionRapidaSaludRecordDetail(registroId);
        if (requestId !== requestSequence.current) return null;
        return applyServerDetail(detail) ? detail : null;
      } catch (loadError) {
        if (requestId === requestSequence.current) {
          setError(getRevisionDetailError(loadError).message);
          if (initial) {
            setServerSnapshot(null);
            setDraft(null);
          }
        }
        return null;
      } finally {
        if (requestId === requestSequence.current) {
          setInitialLoading(false);
          setRefreshing(false);
        }
      }
    },
    [applyServerDetail, enabled, registroId]
  );

  useEffect(() => {
    requestSequence.current += 1;
    setServerSnapshot(null);
    setDraft(null);
    setFieldErrors({});
    setError("");
    setStateDialog(null);

    if (!enabled || registroId === null || registroId === undefined || registroId === "") {
      setInitialLoading(false);
      setRefreshing(false);
      return undefined;
    }

    loadDetail({ initial: true });
    return () => {
      requestSequence.current += 1;
    };
  }, [enabled, loadDetail, registroId]);

  const changes = useMemo(
    () => buildRevisionDetailChanges(serverSnapshot, draft),
    [draft, serverSnapshot]
  );
  const dirty = hasRevisionDetailChanges(changes);
  const status = getRevisionRecordStatus(serverSnapshot);
  const source = serverSnapshot?.source;
  const legacy = source === "LEGACY";
  const fieldsEditable = legacy && serverSnapshot?.sectionCapabilities?.fields?.canEdit === true;
  const diagnosesEditable = legacy && serverSnapshot?.sectionCapabilities?.diagnoses?.canEdit === true;
  const canSave = legacy && serverSnapshot?.capabilities?.canEdit === true;
  const canVerify = legacy && serverSnapshot?.capabilities?.canVerify === true;
  const canMarkError = legacy && serverSnapshot?.capabilities?.canMarkError === true;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const setDraftField = useCallback((section, definition, rawValue) => {
    const value = normalizeRevisionFieldInput(definition, rawValue);
    setDraft((current) => current ? ({
      ...current,
      fields: {
        ...current.fields,
        [section]: {
          ...(current.fields?.[section] || {}),
          [definition.name]: value,
        },
      },
    }) : current);
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[`${section}.${definition.name}`];
      return next;
    });
  }, []);

  const setOrderedSlot = useCallback((key, index, value) => {
    setDraft((current) => current ? ({
      ...current,
      [key]: current[key].map((item, itemIndex) => (itemIndex === index ? value : item)),
    }) : current);
    setFieldErrors((current) => {
      const next = { ...current };
      delete next[`${key}.${index}`];
      return next;
    });
  }, []);

  const resetDraft = useCallback(() => {
    setDraft(createRevisionDetailDraft(serverSnapshot));
    setFieldErrors({});
    setError("");
  }, [serverSnapshot]);

  const executeMutation = useCallback(async (action, options = {}) => {
    if (mutationInFlight.current || !serverSnapshot?.versionToken) return null;

    if (action !== "mark_error") {
      const validation = validateRevisionDetailDraft(draft);
      if (Object.keys(validation).length > 0) {
        setFieldErrors(validation);
        setError("Revisa los campos marcados antes de continuar.");
        return null;
      }
    }

    mutationInFlight.current = true;
    setSaving(true);
    setError("");
    const payload = {
      expectedVersionToken: serverSnapshot.versionToken,
      action,
      ...(action === "mark_error" ? { reason: options.reason } : { changes }),
    };

    try {
      const updated = await updateRevisionRapidaSaludRecord(registroId, payload);
      if (!applyServerDetail(updated)) return null;
      const result = { action, detail: updated, message: SUCCESS_MESSAGES[action] };
      onMutationSuccess?.(result);
      return result;
    } catch (mutationError) {
      const mapped = getRevisionDetailError(mutationError);
      if (mapped.code === "VERSION_CONFLICT" || mapped.code === "INVALID_STATE") {
        setStateDialog(mapped);
      } else {
        setError(mapped.message);
      }
      return null;
    } finally {
      mutationInFlight.current = false;
      setSaving(false);
    }
  }, [applyServerDetail, changes, draft, onMutationSuccess, registroId, serverSnapshot]);

  return {
    serverSnapshot,
    draft,
    setDraft,
    initialLoading,
    refreshing,
    saving,
    error,
    setError,
    fieldErrors,
    setFieldErrors,
    stateDialog,
    setStateDialog,
    changes,
    dirty,
    status,
    source,
    legacy,
    fieldsEditable,
    diagnosesEditable,
    canSave,
    canVerify,
    canMarkError,
    loadDetail,
    setDraftField,
    setOrderedSlot,
    resetDraft,
    executeMutation,
  };
};

export default useRevisionRegistroDraft;
